import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useCallback } from 'react'
import { useMissingCustomerAddressToast } from 'common/hooks'

/**
 * Sending an invoice needs the customer's billing address and the backend
 * rejects it without one. Catch that here instead: the request would fail with
 * a generic "Failed to send invoice" that says nothing about the real problem.
 *
 * Reads the NEXT invoice because that is the version whose billing address can
 * be null — v20240730 always sends a populated one.
 */
export const useInvoiceBillingAddressGate = (invoiceId: string) => {
  const showMissingAddressToast = useMissingCustomerAddressToast()

  const { data: invoice } = useQuery(
    dashboardv99990101Client.getInvoice,
    { id: invoiceId },
    { enabled: Boolean(invoiceId) }
  )

  const isMissingBillingAddress =
    Boolean(invoice) && !invoice?.customerBillingAddress

  /** Returns true when it stopped the send, so the caller can bail. */
  const blockSendWhenMissingAddress = useCallback(() => {
    if (!isMissingBillingAddress) {
      return false
    }

    showMissingAddressToast('sending this invoice')
    return true
  }, [isMissingBillingAddress, showMissingAddressToast])

  return { isMissingBillingAddress, blockSendWhenMissingAddress }
}
